/**
 * L2DoneGlow — chore-card edge glow + check bounce (Plan 02-15 / UI-SPEC §11).
 *
 * Tier L2 fires on `chore.completed` for OWN complete (D-12 — complete
 * is optimistic, so this fires from the submit handler, not from a WS
 * round-trip). The visual effect is a 600ms terracotta-soft outline pulse
 * + check-icon bounce inside the icon-tile.
 *
 * Like L1, this tier dispatches a CustomEvent on the document so the
 * underlying ChoreCard renders the visual without celebrations being
 * coupled to PoolBoardRoute internals. Audio (`done.wav`) still plays
 * via the useCelebration hook (or the submit handler that imports
 * playSfx directly) — that's the primary feedback at tier 2.
 *
 * Reduced-motion: collapses to a no-op.
 *
 * No producer dispatches an L2 event today: useCelebration deliberately drops
 * the `awaiting_approval` frame (D-12 — L2 is meant to fire from the optimistic
 * submit handler) and no submit handler calls it, so CelebrationProvider's
 * `activeL2s` is always empty. L2DoneGlow.test.tsx is the only exercise this
 * component gets, which is why it carries the same shape L1 and L3 do rather
 * than a looser one — whenever the producer lands, the component is already
 * correct.
 *
 * The side effects live in TWO effects and the halves are NOT symmetric —
 * issue #81 established the shape on L3 and #82 applied it here. The full
 * argument is in L3ApprovedBurst.tsx's header; read it before touching either
 * dep array. In brief:
 *
 *   A (the celebration) dispatches `stell:l2` once per instanceId, guarded by
 *   a ref, and MUST NEVER grow a cleanup — the guard is only safe in an effect
 *   that has none.
 *
 *   B (the lifetime) owns the timer and its clearTimeout and MUST NEVER be
 *   ref-guarded: React runs a cleanup BEFORE the re-run it would then skip, so
 *   a guard here clears the timer and arms no replacement — `onComplete` never
 *   fires and the celebration hangs on screen forever.
 *
 * CelebrationProvider passes a fresh `() => removeEvent(ev.id)` arrow on every
 * render, so `onComplete` is reached through a latest-ref (precedent:
 * useCelebration.ts) rather than listed as a dependency.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { TIMINGS } from './timings';

interface L2DoneGlowProps {
  instanceId: string;
  reducedMotion: boolean;
  onComplete: () => void;
}

export function L2DoneGlow({
  instanceId,
  reducedMotion,
  onComplete,
}: L2DoneGlowProps): ReactNode {
  // Latest-ref, so effect B never lists the provider's arrow as a dependency.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const firedFor = useRef<string | null>(null);

  // A — the celebration. One `stell:l2` dispatch per instanceId. See the
  // header: this effect MUST NEVER grow a cleanup, which is what makes the
  // ref-guard safe. It reads `reducedMotion` from the mount-time closure
  // deliberately (useCelebration snapshots it onto the event, so it is
  // immutable for an instance's lifetime anyway).
  useEffect(() => {
    if (firedFor.current === instanceId) return;
    firedFor.current = instanceId;
    if (reducedMotion) return;

    // Decouple celebration → card visual via CustomEvent. ChoreCard (or
    // any future consumer) listens for this event and applies its own
    // outline-pulse + check-bounce animation.
    try {
      const ev = new CustomEvent('stell:l2', {
        detail: { instanceId, durationMs: TIMINGS.l2.glow },
      });
      document.dispatchEvent(ev);
    } catch {
      /* ignore — JSDOM may lack CustomEvent in some configs */
    }
  }, [instanceId]);

  // B — lifetime. Owns the timer and its clearTimeout. See the header: this
  // one MUST NEVER be ref-guarded.
  useEffect(() => {
    if (reducedMotion) {
      onCompleteRef.current();
      return undefined;
    }
    const timer = setTimeout(() => {
      onCompleteRef.current();
    }, TIMINGS.l2.glow);
    return () => {
      clearTimeout(timer);
    };
  }, [instanceId, reducedMotion]);

  return null;
}
